import { useEffect, useRef } from 'react';

import MapFilterButton, {
  type MapFilterButtonHandle,
  type MapFilterButtonProps,
  type MapFilterField,
} from '../features/travel/MapFilterButton';
import { ChromePill, type ChromeSize } from './ChromePill';
import SortToggle, { type SortMode } from './SortToggle';
import type { Layout } from './viewToggleMetrics';

/**
 * The view-specific button (contract §16): one slot at the header's right, one
 * button per layout. Map: the glance button and its popover. List: the sort
 * toggle. Calendar: Share my calendar.
 *
 * The List can ask for the map's popover — Nearest with nowhere to measure
 * from — and the slot then shows the glance button, open on that field, until
 * the popover is dismissed.
 */

interface Props {
  layout: Layout;
  size: ChromeSize;
  map: Omit<MapFilterButtonProps, 'size' | 'onClose' | 'ref'>;
  sort: SortMode;
  onFlipSort: () => void;
  sortBusy?: boolean;
  onShare: () => void;
  /** The map's popover, asked for from another layout; null when it isn't. */
  ask: MapFilterField | null;
  onAskDone: () => void;
}

export default function HeaderAction({ layout, size, map, sort, onFlipSort, sortBusy, onShare, ask, onAskDone }: Props) {
  const glance = useRef<MapFilterButtonHandle | null>(null);
  const borrowed = layout !== 'map' && ask !== null;

  // Opened once it is on screen, from wherever the ask came.
  useEffect(() => {
    if (ask) glance.current?.onOpen(ask);
  }, [ask]);

  if (layout === 'map' || borrowed) {
    return <MapFilterButton ref={glance} {...map} size={size} onClose={ask ? onAskDone : undefined} />;
  }
  if (layout === 'list') return <SortToggle sort={sort} size={size} onFlip={onFlipSort} busy={sortBusy} />;
  return (
    <ChromePill type="button" $size={size} onClick={onShare} aria-haspopup="dialog">
      Share my calendar
    </ChromePill>
  );
}
